import { PaymentIntentResult } from '@stripe/stripe-js';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import { Modal } from '../../../../components/modal';
import { useState } from 'react';
import { useSnapshot } from 'valtio';
import { authStore } from '../../../../../store/authStore';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import { PaymentElementSkeleton } from '../../../../components/skeletons';

const FormShell = styled.div`
  ${tw`w-full max-w-full box-border pt-1 pb-2`}
`;

const PaymentElementContainer = styled.div`
  ${tw`w-full max-w-full box-border overflow-x-hidden`}

  /* Keep Stripe Payment Element within the modal content box */
  & > div {
    ${tw`w-full max-w-full`}
  }
`;

const BookButton = styled.button`
  ${tw`
    w-full
    text-white
    py-3
    rounded-lg
    font-semibold
    transition-colors
    mt-4
  `}

  ${({ disabled }) => (disabled ? tw`bg-gray-400 cursor-not-allowed` : tw`bg-black cursor-pointer`)}
  ${({ disabled }) =>
    !disabled &&
    css`
      &:hover {
        ${tw`bg-purple-700`}
      }
    `}
`;
const NameField = styled.div`
  ${tw`mt-3`}
`;

const NameLabel = styled.label`
  ${tw`block text-xs font-medium mb-1`}
  color: var(--color-text-secondary);
`;

const NameInput = styled.input`
  ${tw`w-full rounded-md px-3 py-2 text-sm`}
`;

/** The account's name as Stripe should see it on the card. */
export function accountNameOf(
  user:
    | { firstName?: string | null; lastName?: string | null; displayName?: string | null }
    | null
    | undefined,
): string {
  const full = [user?.firstName, user?.lastName]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
  return full || user?.displayName?.trim() || '';
}

const CardNotice = styled.p`
  ${tw`text-xs mt-3 mb-0`}
  color: var(--color-text-secondary);
`;

// Payment form component
export const PaymentForm = ({
  isOpen,
  onClose,
  onPaymentFailedCallback,
  onPaymentSuccessCallback,
  beforeTransactionFinish,
  beforeConfirm,
  clientSecret,
}: {
  isOpen: boolean;
  onClose: () => void;
  onPaymentFailedCallback: (error: any) => void;
  onPaymentSuccessCallback: (data: any) => void;
  beforeTransactionFinish: () => Promise<any>;
  /**
   * Asked when the guest presses Pay, before the card is sent to Stripe. False stops
   * here and closes the form (spec 023: the site is not taking bookings yet).
   */
  beforeConfirm?: () => Promise<boolean>;
  clientSecret?: string;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isComplete, setIsComplete] = useState(false);
  // A saved card picked from the Payment Element already carries its name; the name
  // field is only for a card entered now (spec 013 FR-030).
  const [savedCardSelected, setSavedCardSelected] = useState(false);
  const [paymentFormReady, setPaymentFormReady] = useState(false);

  const { user } = useSnapshot(authStore);
  // Prefilled from the account so an honest guest only confirms it (spec 013 FR-030).
  const [cardholderName, setCardholderName] = useState(() => accountNameOf(user));

  const newCardBillingDetails = () => ({
    billing_details: {
      email: user?.email,
      name: cardholderName.trim(),
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    try {
      if (beforeConfirm && !(await beforeConfirm())) return;

      // Detect if we're using SetupIntent or PaymentIntent based on clientSecret prefix
      const isSetupMode = clientSecret?.startsWith('seti_');

      if (isSetupMode) {
        // Setup mode: collect payment method with SetupIntent
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          redirect: 'if_required',
          confirmParams: {
            return_url: window.location.href,
            ...(savedCardSelected ? {} : { payment_method_data: newCardBillingDetails() }),
          },
        });

        if (error) {
          toast.error(error.message);
          throw error;
        }

        if (setupIntent && setupIntent.status === 'succeeded') {
          await beforeTransactionFinish();
          onPaymentSuccessCallback({ setupIntent });
        }
      } else {
        // Payment mode: confirm the payment
        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          redirect: 'if_required',
          confirmParams: {
            ...(savedCardSelected ? {} : { payment_method_data: newCardBillingDetails() }),
          },
        });

        if (error) {
          toast.error(error.message);
          throw error;
        }

        // Only if payment is successful, call beforeTransactionFinish
        if (paymentIntent && paymentIntent.status === 'succeeded') {
          await beforeTransactionFinish();
          onPaymentSuccessCallback({ paymentIntent });
        }
      }
    } catch (error) {
      onPaymentFailedCallback(error);
    } finally {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} modalWidth="min(480px, calc(100vw - 2rem))">
      <FormShell>
        <form onSubmit={handleSubmit}>
          <PaymentElementContainer>
            <PaymentElement
              // Spec 013 FR-030: the cardholder name is collected in our own field below
              // (the Payment Element cannot be told to always ask for it) and passed with
              // the account email at confirmation.
              options={{ fields: { billingDetails: { name: 'never', email: 'never' } } }}
              onReady={() => {
                setPaymentFormReady(true);
              }}
              onChange={event => {
                setIsComplete(event.complete);
                setSavedCardSelected(Boolean(event.value?.payment_method));
              }}
            />
          </PaymentElementContainer>
          {paymentFormReady && !savedCardSelected ? (
            <NameField>
              <NameLabel htmlFor="cardholder-name">Name on card</NameLabel>
              <NameInput
                id="cardholder-name"
                name="cardholderName"
                autoComplete="cc-name"
                value={cardholderName}
                onChange={e => setCardholderName(e.target.value)}
                placeholder="As printed on the card"
              />
            </NameField>
          ) : null}
          {paymentFormReady ? (
            <CardNotice>
              Use a card in your own name. This card is used for this trip&apos;s security deposit,
              any extension you request, and charges after the trip under the rental agreement. Tick
              &quot;Save&quot; above to use it for future trips as well.
            </CardNotice>
          ) : null}

          {paymentFormReady ? (
            <BookButton
              type="submit"
              disabled={!stripe || !isComplete || (!savedCardSelected && !cardholderName.trim())}
            >
              Pay now
            </BookButton>
          ) : (
            <PaymentElementSkeleton />
          )}
        </form>
      </FormShell>
    </Modal>
  );
};
